//#region ENUM AllMessageTypesEnum
/**
 * ### AllMessageTypesEnum
 * Enum representing all message types.
 *
 * This enum is accompanied by:
 * - `AllMessageTypesEnumHumanReadable` — human-friendly labels.
 * - `AllMessageTypesEnumType` — a union type of all enum keys.
 *
 * @enum AllMessageTypesEnum
 * @property ACCESS_CHANGE - Represents an access change notification.
 * @property AUTO_ORDERS - Represents an auto orders notification.
 * @property AUTO_ORDER_RESULT - Represents an auto order result notification.
 * @property AUTO_ORDERS_U_UPDATE - Represents an auto orders user update notification.
 * @property LOADING - Represents a loading state.
 * @property SERVER_CONFIG - Represents a server configuration notification.
 * @property SERVER_TEST - Represents a server test notification.
 * @property SIGN_UP - Represents a sign up notification.
 * @property STOCK_UPDATE - Represents a stock update notification.
 */
export enum AllMessageTypesEnum {
  /** Represents an access change notification. */
  ACCESS_CHANGE = 'ACCESS_CHANGE',

  /** Represents an auto orders notification. */
  AUTO_ORDERS = 'AUTO_ORDERS',

  /** Represents an auto order result notification. */
  AUTO_ORDER_RESULT = 'AUTO_ORDER_RESULT',

  /** Represents an auto orders user update notification. */
  AUTO_ORDERS_U_UPDATE = 'AUTO_ORDERS_U_UPDATE',

  /** Represents a loading state. */
  LOADING = 'LOADING',

  /** Represents a quotes notification. */
  QUOTES = 'QUOTES',

  /** Represents a server configuration notification. */
  SERVER_CONFIG = 'SERVER_CONFIG',

  /** Represents a server test notification. */
  SERVER_TEST = 'SERVER_TEST',

  /** Represents a signup notification. */
  SIGN_UP = 'SIGN_UP',

  /** Represents a stock update notification. */
  STOCK_UPDATE = 'STOCK_UPDATE',

  /** Represents a stock take notification. */
  STOCK_TAKE = 'STOCK_TAKE',
}
//#endregion

//#region ENUM AllMessageTypesEnumHumanReadable
/**
 * ### AllMessageTypesEnumHumanReadable
 * Human-readable labels for each member of `AllMessageTypesEnum`.
 *
 * @enum AllMessageTypesEnumHumanReadable
 * @property ACCESS_CHANGE - Label for access change notifications.
 * @property AUTO_ORDERS - Label for auto orders notifications.
 * @property AUTO_ORDER_RESULT - Label for auto order result notifications.
 * @property AUTO_ORDERS_U_UPDATE - Label for auto orders user update notifications.
 * @property LOADING - Label used during loading state.
 * @property SERVER_CONFIG - Label for server configuration notifications.
 * @property SERVER_TEST - Label for server test notifications.
 * @property SIGN_UP - Label for sign up notifications.
 * @property STOCK_UPDATE - Label for stock update notifications.
 */
export enum AllMessageTypesEnumHumanReadable {
  /** Label for access change notifications. */
  ACCESS_CHANGE = 'Access Change',

  /** Label for auto orders notifications. */
  AUTO_ORDERS = 'Auto Orders',

  /** Label for auto order result notifications. */
  AUTO_ORDER_RESULT = 'Auto Order Result',

  /** Label for auto orders user update notifications. */
  AUTO_ORDERS_U_UPDATE = 'Auto Orders User Update',

  /** Label used during loading state. */
  LOADING = 'Loading',

  /** Label for quotes notifications. */
  QUOTES = 'Quotes',

  /** Label for server configuration notifications. */
  SERVER_CONFIG = 'Server Config',

  /** Label for server test notifications. */
  SERVER_TEST = 'Server Test',

  /** Label for sign up notifications. */
  SIGN_UP = 'Sign Up',

  /** Label for stock update notifications. */
  STOCK_UPDATE = 'Stock Update',

  /** Label for stock take notifications. */
  STOCK_TAKE = 'Stock Take',
}
//#endregion

//#region TYPE AllMessageTypesEnumType
/**
 * ### AllMessageTypesEnumType
 * Union type of all keys in `AllMessageTypesEnum`.
 *
 * @type AllMessageTypesEnumType
 * @example
 * // Possible values:
 * // 'ACCESS_CHANGE' | 'AUTO_ORDERS' | 'AUTO_ORDER_RESULT' | 'AUTO_ORDERS_U_UPDATE' |
 * // 'LOADING' | 'SERVER_CONFIG' | 'SERVER_TEST' | 'SIGN_UP' | 'STOCK_UPDATE'
 */
export type AllMessageTypesEnumType = keyof typeof AllMessageTypesEnum;
//#endregion



//#region ENUM NotificationTypeEnum
/**
 * ### NotificationTypeEnum
 * Enum representing different types of notifications.
 *
 * @enum NotificationTypeEnum
 * @property {string} AUTO_ORDERS - Represents a notification for auto orders.
 * @property {string} LOADING - Represents a loading state (internal use).
 * @property {string} STOCK_UPDATE - Represents a notification for stock updates.
 */
export enum NotificationTypeEnum {
  /** Represents a notification for auto orders. */
  AUTO_ORDERS = 'AUTO_ORDERS',

  /** Represents a loading state (internal use). */
  LOADING = 'LOADING',

  /** Represents a notification for quotes. */
  QUOTES = 'QUOTES',

  /** Represents a notification for stock updates. */
  STOCK_UPDATE = 'STOCK_UPDATE',

  /** Represents a notification for stock takes. */
  STOCK_TAKE = 'STOCK_TAKE',
}

//#endregion

//#region ENUM NotificationTypeEnumHumanReadable
/**
 * ### NotificationTypeEnumHumanReadable
 * Enum representing human-readable labels for notification types.
 *
 * @enum NotificationTypeEnumHumanReadable
 * @property {string} AUTO_ORDERS - Label for auto order notifications.
 * @property {string} LOADING - Label used during loading state.
 * @property {string} STOCK_UPDATE - Label for stock update notifications.
 */
export enum NotificationTypeEnumHumanReadable {
  /** Label for auto order notifications. */
  AUTO_ORDERS = 'Auto Order',

  /** Label used during loading state. */
  LOADING = 'Loading',

  /** Label for quote notifications. */
  QUOTES = 'Quotes',

  /** Label for stock update notifications. */
  STOCK_UPDATE = 'Stock Update',

  /** Label for stock take notifications. */
  STOCK_TAKE = 'Stock Take',
}

//#endregion

//#region TYPE NotificationType
/**
 * ### NotificationType
 * Type representing the keys of `NotificationTypeEnum`.
 *
 * This type includes all possible keys from the `NotificationTypeEnum`, which currently are:
 * - `'AUTO_ORDERS'`
 * - `'LOADING'`
 * - `'STOCK_UPDATE'`
 *
 * @type NotificationType
 * @example
 * const type: NotificationType = 'AUTO_ORDERS';
 */
export type NotificationType = keyof typeof NotificationTypeEnum;
//#endregion

